import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import KpiBadge from "../kpi/KpiBadge";
import GeorgeOverviewDrawer from "./GeorgeOverviewDrawer";
import Button from "../../ui/Button";
import PageState from "../../ui/PageState";
import Select from "../../ui/Select";
import Table from "../../ui/Table";
import StatusBadge from "../../ui/StatusBadge";
import { useAuth } from "../../useAuth";
import { listAdminUsers } from "../../api/admin.api";
import { RoleEnum, isRole } from "../../constants/roles";
import { usePinnedItems } from "../../hooks/usePinnedItems";
import { buildPinnedItemRoute } from "../../features/pins/pinnedItemRoutes";
import PinnedItemDetailDrawer, { supportsPinnedItemDrawer } from "../../features/pins/PinnedItemDetailDrawer";

const MODULE_ORDER = ["risk", "control", "action", "incident", "tenant_policy", "audit_engagement", "audit_universe", "kpi"];

const shellStyle = {
  display: "grid",
  gap: "16px",
};

const cardStyle = {
  background: "white",
  borderRadius: "12px",
  border: "1px solid #e5e7eb",
  padding: "16px",
};

const emptyStateStyle = {
  border: "1px dashed #d1d5db",
  borderRadius: "12px",
  padding: "18px",
  background: "#f9fafb",
  display: "grid",
  gap: "6px",
};

const groupStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  background: "#fcfcfd",
  overflow: "hidden",
};

const groupHeaderStyle = {
  padding: "12px 14px",
  borderBottom: "1px solid #eaecf0",
  background: "#f8fafc",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
};

const groupBodyStyle = {
  padding: "14px",
  background: "#ffffff",
};

const actionCellStyle = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
};

const viewerBannerStyle = {
  borderRadius: "12px",
  border: "1px solid #d6f5f1",
  background: "#f4fffd",
  padding: "14px 16px",
  display: "grid",
  gap: "4px",
};

const formatDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDate = (value) => formatDateTime(value) || "-";

const scoreChipStyle = (score) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "4px 10px",
  borderRadius: 999,
  background: score >= 16 ? "#fecaca" : score >= 9 ? "#fde68a" : "#bfdbfe",
  color: "#111827",
  fontSize: "12px",
  fontWeight: 700,
  minWidth: 44,
});

const renderStatusBadge = (value) => (value ? <StatusBadge variant="status" value={value} fallbackLabel={value} /> : "-");
const renderPriorityBadge = (value) =>
  value ? <StatusBadge variant="severity" value={value} fallbackLabel={value} label={value} /> : "-";

const renderPolicyStatusBadge = (value) => {
  if (!value) return "-";
  const retired = String(value).toLowerCase() === "retired";
  return <StatusBadge variant="kpi" value={retired ? "stale" : "green"} label={retired ? "Retired" : "Active"} />;
};

const renderPublicationBadge = (value) => {
  if (!value) return "-";
  const published = String(value).toLowerCase() === "published";
  return <StatusBadge value={published ? "published" : "draft"} label={published ? "Published" : "Unpublished"} />;
};

const formatUserOptionLabel = (entry, { includeYouMarker = false } = {}) => {
  if (!entry) return "Unknown user";
  const baseLabel = entry.email ? `${entry.username} (${entry.email})` : entry.username || `User #${entry.id}`;
  return includeYouMarker ? `${baseLabel} (you)` : baseLabel;
};

function buildColumnsForItemType(itemType, { navigate, openItem, unpinItem, readOnly }) {
  const actionsColumn = {
    id: "__actions",
    header: "Actions",
    render: (item) => {
      const route = buildPinnedItemRoute(item.item_type, item.item_id);
      return (
        <div style={actionCellStyle} onClick={(event) => event.stopPropagation()}>
          <Button type="button" variant="secondary" onClick={() => route && navigate(route)} disabled={!route}>
            Open page
          </Button>
          {!readOnly && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => unpinItem({ itemType: item.item_type, itemId: item.item_id }).catch(() => {})}
            >
              Unpin
            </Button>
          )}
        </div>
      );
    },
  };

  if (itemType === "risk") {
    return [
      { id: "category", header: "Category", accessor: (item) => item.display_fields?.category || "-" },
      { id: "name", header: "Name", accessor: (item) => item.title || "-" },
      { id: "status", header: "Status", accessor: (item) => renderStatusBadge(item.display_fields?.status || item.status) },
      {
        id: "score",
        header: "Residual Score",
        accessor: (item) => {
          const raw = item.display_fields?.score;
          const score = Number(raw);
          if (!Number.isFinite(score)) return "-";
          return <span style={scoreChipStyle(score)}>{raw}</span>;
        },
      },
      {
        id: "appetite",
        header: "Appetite",
        accessor: (item) => {
          const value = item.display_fields?.appetite;
          return value ? renderStatusBadge(value) : "-";
        },
      },
      {
        id: "treatment",
        header: "Treatment",
        accessor: (item) => {
          const value = item.display_fields?.treatment;
          return value ? renderStatusBadge(value) : "-";
        },
      },
      actionsColumn,
    ];
  }

  if (itemType === "control") {
    return [
      { id: "name", header: "Name", accessor: (item) => item.display_fields?.name || item.title || "-" },
      { id: "description", header: "Description", accessor: (item) => item.display_fields?.description || "-" },
      { id: "status", header: "Status", accessor: (item) => renderStatusBadge(item.display_fields?.status || item.status) },
      {
        id: "effectiveness",
        header: "Effectiveness",
        accessor: (item) => {
          const value = item.display_fields?.effectiveness;
          return value ? renderStatusBadge(value) : "-";
        },
      },
      { id: "owner", header: "Owner", accessor: (item) => item.display_fields?.owner || item.owner_display || "-" },
      { id: "catalog_id", header: "Catalog ID", accessor: (item) => item.display_fields?.catalog_id || "-" },
      { id: "category", header: "Category", accessor: (item) => item.display_fields?.category || "-" },
      { id: "updated", header: "Updated", accessor: (item) => formatDate(item.display_fields?.updated || item.updated_at) },
      actionsColumn,
    ];
  }

  if (itemType === "action") {
    return [
      { id: "title", header: "Action", accessor: (item) => item.display_fields?.title || item.title || "-" },
      { id: "status", header: "Status", accessor: (item) => renderStatusBadge(item.display_fields?.status || item.status) },
      { id: "priority", header: "Priority", accessor: (item) => renderPriorityBadge(item.display_fields?.priority) },
      { id: "category", header: "Category", accessor: (item) => item.display_fields?.category || "-" },
      { id: "due_date", header: "Due", accessor: (item) => formatDate(item.display_fields?.due_date) },
      { id: "owner", header: "Owner", accessor: (item) => item.display_fields?.owner || item.owner_display || "-" },
      { id: "assignee", header: "Assignee", accessor: (item) => item.display_fields?.assignee || "-" },
      actionsColumn,
    ];
  }

  if (itemType === "incident") {
    return [
      { id: "title", header: "Incident", accessor: (item) => item.display_fields?.title || item.title || "-" },
      { id: "status", header: "Status", accessor: (item) => renderStatusBadge(item.display_fields?.status || item.status) },
      { id: "severity", header: "Severity", accessor: (item) => renderPriorityBadge(item.display_fields?.severity) },
      { id: "priority", header: "Priority", accessor: (item) => renderPriorityBadge(item.display_fields?.priority) },
      { id: "category", header: "Category", accessor: (item) => item.display_fields?.category || "-" },
      { id: "owner", header: "Owner", accessor: (item) => item.display_fields?.owner || item.owner_display || "-" },
      { id: "reported_at", header: "Reported", accessor: (item) => formatDate(item.display_fields?.reported_at) },
      actionsColumn,
    ];
  }

  if (itemType === "tenant_policy") {
    return [
      { id: "name", header: "Name", accessor: (item) => item.display_fields?.name || item.title || "-" },
      { id: "owner", header: "Owner", accessor: (item) => item.display_fields?.owner || item.owner_display || "Unassigned" },
      { id: "status", header: "Status", accessor: (item) => renderPolicyStatusBadge(item.display_fields?.status || item.status) },
      { id: "publication", header: "Publication", accessor: (item) => renderPublicationBadge(item.display_fields?.publication) },
      actionsColumn,
    ];
  }

  if (itemType === "audit_engagement") {
    return [
      { id: "title", header: "Title", accessor: (item) => item.display_fields?.title || item.title || "-" },
      { id: "status", header: "Status", accessor: (item) => item.display_fields?.status || item.status || "-" },
      { id: "current_step", header: "Current Step", accessor: (item) => item.display_fields?.current_step || "-" },
      { id: "lead", header: "Lead", accessor: (item) => item.display_fields?.lead || item.owner_display || "-" },
      { id: "auditable_entity", header: "Auditable Entity", accessor: (item) => item.display_fields?.auditable_entity || "-" },
      actionsColumn,
    ];
  }

  if (itemType === "audit_universe") {
    return [
      { id: "name", header: "Name", accessor: (item) => item.display_fields?.name || item.title || "-" },
      { id: "entity_type", header: "Entity Type", accessor: (item) => item.display_fields?.entity_type || "-" },
      { id: "business_area", header: "Business Area", accessor: (item) => item.display_fields?.business_area || "-" },
      { id: "risk_rating", header: "Risk Rating", accessor: (item) => item.display_fields?.risk_rating || "-" },
      {
        id: "priority",
        header: "Priority",
        accessor: (item) => {
          const value = item.display_fields?.priority;
          return value ? renderStatusBadge(value) : "-";
        },
      },
      { id: "owner", header: "Primary Owner", accessor: (item) => item.display_fields?.owner || item.owner_display || "-" },
      actionsColumn,
    ];
  }

  if (itemType === "kpi") {
    return [
      { id: "name", header: "Name", accessor: (item) => item.display_fields?.name || item.title || "-" },
      { id: "latest", header: "Latest", accessor: (item) => item.display_fields?.latest || "--" },
      {
        id: "rag",
        header: "Status (Red/Amber/Green)",
        accessor: (item) => {
          const value = item.display_fields?.rag;
          return value ? <KpiBadge value={value} /> : "--";
        },
      },
      { id: "owner", header: "Owner", accessor: (item) => item.display_fields?.owner || item.owner_display || "--" },
      { id: "category", header: "Category", accessor: (item) => item.display_fields?.category || "--" },
      { id: "updated", header: "Updated", accessor: (item) => formatDate(item.display_fields?.updated || item.updated_at) },
      actionsColumn,
    ];
  }

  return [
    { id: "title", header: "Title", accessor: (item) => item.title || "-" },
    { id: "status", header: "Status", accessor: (item) => item.status || "-" },
    { id: "owner", header: "Owner", accessor: (item) => item.owner_display || "-" },
    actionsColumn,
  ];
}

export default function MyOverview() {
  const navigate = useNavigate();
  const auth = useAuth();
  const user = auth?.user || null;
  const isAdminViewer = isRole(user?.role, RoleEnum.ADMIN);
  const [userOptions, setUserOptions] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [userOptionsError, setUserOptionsError] = useState(null);
  const [selectedOverviewUserId, setSelectedOverviewUserId] = useState("self");
  const [activeItemKey, setActiveItemKey] = useState(null);
  const [georgeOpen, setGeorgeOpen] = useState(false);
  const viewedUserId = selectedOverviewUserId === "self" ? null : selectedOverviewUserId;
  const isViewingAnotherUser = selectedOverviewUserId !== "self";
  const { items, isLoading, error, unpinItem } = usePinnedItems({ viewUserId: viewedUserId });

  useEffect(() => {
    if (!isAdminViewer) {
      setUserOptions([]);
      setIsLoadingUsers(false);
      setUserOptionsError(null);
      setSelectedOverviewUserId("self");
      return;
    }

    let isCancelled = false;
    setIsLoadingUsers(true);
    setUserOptionsError(null);

    listAdminUsers()
      .then((response) => {
        if (isCancelled) return;
        const resolvedUsers = Array.isArray(response?.data) ? response.data : [];
        setUserOptions(resolvedUsers);
        setSelectedOverviewUserId((current) => {
          if (current === "self") return current;
          return resolvedUsers.some((entry) => String(entry.id) === String(current)) ? current : "self";
        });
      })
      .catch((nextError) => {
        if (isCancelled) return;
        setUserOptions([]);
        setUserOptionsError(nextError);
        setSelectedOverviewUserId("self");
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingUsers(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [isAdminViewer]);

  useEffect(() => {
    setActiveItemKey(null);
    setGeorgeOpen(false);
  }, [selectedOverviewUserId, user?.id, user?.username, user?.tenant_id]);

  const activeItem = useMemo(
    () => items.find((item) => `${item.item_type}:${item.item_id}` === activeItemKey) || null,
    [activeItemKey, items]
  );

  const currentAdminUserOption = useMemo(() => {
    if (!user?.username) return null;
    return userOptions.find((entry) => entry.username === user.username) || null;
  }, [user?.username, userOptions]);

  const viewedUser = useMemo(() => {
    if (selectedOverviewUserId === "self") {
      return currentAdminUserOption;
    }
    return userOptions.find((entry) => String(entry.id) === String(selectedOverviewUserId)) || null;
  }, [currentAdminUserOption, selectedOverviewUserId, userOptions]);

  const viewedOverviewLabel = useMemo(() => {
    if (selectedOverviewUserId === "self") {
      if (currentAdminUserOption) {
        return formatUserOptionLabel(currentAdminUserOption, { includeYouMarker: true });
      }
      if (user?.username) {
        return `${user.username} (you)`;
      }
      return "Your overview";
    }
    return formatUserOptionLabel(viewedUser);
  }, [currentAdminUserOption, selectedOverviewUserId, user?.username, viewedUser]);

  const selectorOptions = useMemo(() => {
    const options = [{ value: "self", label: "My overview (you)" }];
    const filteredUsers = userOptions
      .filter((entry) => String(entry.username || "").trim())
      .filter((entry) => entry.username !== user?.username)
      .sort((left, right) => formatUserOptionLabel(left).localeCompare(formatUserOptionLabel(right)));

    return options.concat(
      filteredUsers.map((entry) => ({
        value: String(entry.id),
        label: formatUserOptionLabel(entry),
      }))
    );
  }, [user?.username, userOptions]);

  const groupedItems = useMemo(() => {
    const groups = new Map();

    items.forEach((item) => {
      if (!groups.has(item.item_type)) {
        groups.set(item.item_type, {
          itemType: item.item_type,
          moduleLabel: item.module_label,
          items: [],
        });
      }
      groups.get(item.item_type).items.push(item);
    });

    return Array.from(groups.values()).sort((a, b) => {
      const aIndex = MODULE_ORDER.indexOf(a.itemType);
      const bIndex = MODULE_ORDER.indexOf(b.itemType);
      const safeAIndex = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
      const safeBIndex = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
      if (safeAIndex !== safeBIndex) return safeAIndex - safeBIndex;
      return String(a.moduleLabel || a.itemType).localeCompare(String(b.moduleLabel || b.itemType));
    });
  }, [items]);

  const openItem = (item) => {
    if (!item) return;
    if (supportsPinnedItemDrawer(item.item_type)) {
      setActiveItemKey(`${item.item_type}:${item.item_id}`);
      return;
    }

    const route = buildPinnedItemRoute(item.item_type, item.item_id);
    if (route) {
      navigate(route);
    }
  };

  const groupedColumns = Object.fromEntries(
    groupedItems.map((group) => [
      group.itemType,
      buildColumnsForItemType(group.itemType, {
        navigate,
        openItem,
        unpinItem,
        readOnly: isViewingAnotherUser,
      }),
    ])
  );

  return (
    <div style={shellStyle}>
      <section style={cardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "16px",
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: "240px", flex: "1 1 320px" }}>
            <h2 style={{ margin: 0, marginBottom: "6px" }}>My Overview</h2>
            <p style={{ margin: 0, color: "#475467", lineHeight: 1.5 }}>
              A personal workspace for records you want to keep close without changing ownership, workflow, or visibility.
            </p>
          </div>
          {!isViewingAnotherUser && (
            <Button type="button" onClick={() => { setActiveItemKey(null); setGeorgeOpen(true); }}>
              Ask George
            </Button>
          )}
          {isAdminViewer && (
            <div style={{ minWidth: "280px", flex: "0 1 360px" }}>
              <Select
                label="View overview for"
                ariaLabel="View overview for"
                value={selectedOverviewUserId}
                onChange={(event) => setSelectedOverviewUserId(event.target.value)}
                options={selectorOptions}
                loading={isLoadingUsers}
                disabled={Boolean(userOptionsError)}
                emptyLabel="No users available"
                style={{ width: "100%" }}
              />
              {userOptionsError && (
                <div style={{ marginTop: "6px", color: "#b91c1c", fontSize: "12px" }}>
                  {userOptionsError?.response?.data?.detail || userOptionsError?.message || "Unable to load users."}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={viewerBannerStyle}>
        <div
          style={{
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#0f766e",
          }}
        >
          Currently Viewing
        </div>
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#111827" }}>{viewedOverviewLabel}</div>
        <p style={{ margin: 0, color: "#475467", lineHeight: 1.5 }}>
          {isViewingAnotherUser
            ? "Read-only preview of what this user has pinned on their own My Overview page."
            : "This is your personal My Overview workspace."}
        </p>
      </section>

      <section style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center" }}>
          <div>
            <h3 style={{ margin: 0 }}>{isViewingAnotherUser ? "Pinned items in this overview" : "Pinned items"}</h3>
            <div style={{ marginTop: "4px", color: "#6b7280", fontSize: "13px" }}>
              Most recently pinned items appear first.
            </div>
          </div>
          {items.length > 0 && (
            <div style={{ color: "#6b7280", fontSize: "13px" }}>
              {items.length} pinned {items.length === 1 ? "item" : "items"}
            </div>
          )}
        </div>

        {isLoading && (
          <PageState
            state="loading"
            align="left"
            compact
            title="Loading pinned items"
            description="Glyder is preparing the saved records for this overview."
            style={{ marginTop: "16px" }}
          />
        )}
        {!isLoading && !error && items.length === 0 && (
          <div style={{ ...emptyStateStyle, marginTop: "16px" }}>
            <div style={{ fontWeight: 600 }}>No pinned items yet</div>
            <div style={{ color: "#6b7280", fontSize: "13px" }}>
              {isViewingAnotherUser
                ? "This user has not pinned any items to their My Overview page."
                : 'Use "Pin to My Overview" on a supported record detail page to keep it here.'}
            </div>
          </div>
        )}
        {!isLoading && error && (
          <PageState
            state="error"
            align="left"
            compact
            title="Pinned items are unavailable"
            description={error?.response?.data?.detail || error?.message || "Unable to load pinned items."}
            style={{ marginTop: "16px" }}
          />
        )}
        {!isLoading && !error && items.length > 0 && (
          <div style={{ marginTop: "12px", display: "grid", gap: "12px" }}>
            {groupedItems.map((group) => (
              <section key={group.itemType} style={groupStyle}>
                <div style={groupHeaderStyle}>
                  <div style={{ fontWeight: 700, color: "#111827" }}>{group.moduleLabel}</div>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    {group.items.length} {group.items.length === 1 ? "item" : "items"}
                  </div>
                </div>
                <div style={groupBodyStyle}>
                  <Table
                    columns={groupedColumns[group.itemType] || []}
                    data={group.items}
                    emptyState={`No ${String(group.moduleLabel || "items").toLowerCase()} pinned.`}
                    getRowKey={(item) => `${item.item_type}:${item.item_id}`}
                    onRowClick={openItem}
                    getRowProps={(item) => ({
                      "aria-label": `Open ${item.title} in a side panel`,
                    })}
                  />
                </div>
              </section>
            ))}
          </div>
        )}
      </section>
      {georgeOpen && !isViewingAnotherUser && (
        <GeorgeOverviewDrawer
          key={`${user?.id || user?.username}:${user?.tenant_id || ""}`}
          onClose={() => setGeorgeOpen(false)}
        />
      )}
      <PinnedItemDetailDrawer
        item={activeItem}
        onClose={() => setActiveItemKey(null)}
        onOpenFullPage={(itemId) => {
          if (!activeItem) return;
          const route = buildPinnedItemRoute(activeItem.item_type, itemId ?? activeItem.item_id);
          if (route) {
            navigate(route);
          }
        }}
      />
    </div>
  );
}
